import type { JSX } from 'preact';
import { useAnimation } from '../UNSAFE_useAnimation';
import { useComponentTheme } from '../UNSAFE_useComponentTheme';
import { useEffect, useRef } from 'preact/hooks';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import {
  TooltipContentVariantOptions,
  TooltipContentStyles
} from './themes/TooltipContentStyles.css';

import { useTestId, TestIdProps } from '../UNSAFE_useTestId';
import { useThemeInterpolations } from '../UNSAFE_useThemeInterpolations';
import { classNames } from '../../utils/UNSAFE_classNames';
import { TooltipContentRedwoodTheme } from './themes/redwood/TooltipContentTheme';

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'children'>;

type Status = 'mounted' | 'unmounted';

type ContentProps = IntrinsicProps & {
  id: string;
  isOpen: boolean;
  isDatatip: boolean;
  onTransitionEnd?: () => void;
} & TestIdProps;

export const TooltipContent = ({
  children,
  id,
  isOpen,
  isDatatip,
  onTransitionEnd,
  testId,
  ...props
}: ContentProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const testIdProps = useTestId(testId);

  const { baseTheme, variantClasses, styles } = useComponentTheme<
    TooltipContentVariantOptions,
    TooltipContentStyles
  >(TooltipContentRedwoodTheme, {
    variant: isDatatip ? 'datatip' : 'tooltip'
  });

  const schemeClass = useThemeInterpolations();

  const { nodeRef } = useAnimation<Status, HTMLDivElement>(isOpen ? 'mounted' : 'unmounted', {
    animationStates: {
      mounted: (_node) => {
        const childNode = _node.firstChild as HTMLDivElement;
        return {
          from: {
            maxHeight: '0',
            opacity: '0%'
          },
          to: {
            maxHeight: `${childNode?.offsetHeight}px`,
            opacity: isDatatip ? '100%' : '95%'
          },
          options: {
            duration: isDatatip ? 1 : 150,
            easing: [0.0, 0.0, 0.2, 1]
          }
        };
      },
      unmounted: (_node) => {
        return {
          from: {
            opacity: isDatatip ? '100%' : '95%'
          },
          to: {
            maxHeight: '0',
            opacity: '0%'
          },
          options: {
            duration: isDatatip ? 1 : 150,
            easing: [0.0, 0.0, 0.2, 1]
          }
        };
      }
    },
    isAnimatedOnMount: true,
    onAnimationEnd: () => onTransitionEnd?.()
  });

  // Set up initial wrapper height so Tooltip can animate from the bottom to the top
  useEffect(() => {
    if (wrapperRef.current && popoverRef.current?.offsetHeight) {
      wrapperRef.current.style.height = `${popoverRef.current?.offsetHeight}px`;
    }
  }, []);

  return (
    <div
      ref={wrapperRef}
      id={id}
      role="tooltip"
      class={classNames([styles.wrapper, schemeClass, baseTheme])}
      {...testIdProps}>
      <div ref={nodeRef} class={styles.inner} {...props}>
        <div ref={popoverRef} class={classNames([variantClasses, styles.content])}>
          {children}
        </div>
      </div>
    </div>
  );
};
